import { getConfig } from '../config';

type TokenProvider = () => Promise<string | undefined>;

let tokenProvider: TokenProvider = async () => undefined;

/** Wired up by AuthProvider so this module has no React dependency. */
export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** Field-keyed validation messages, ready to bind to form inputs. */
    public readonly fields?: Record<string, string[]>,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const token = await tokenProvider();
  if (!token) throw new ApiError(401, 'unauthenticated', 'Your session has expired');

  const res = await fetch(`${getConfig().apiUrl}${path}`, {
    method: options.method ?? 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  });

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {};

  if (!res.ok) {
    const error = (payload.error ?? {}) as { code?: string; message?: string };
    throw new ApiError(
      res.status,
      error.code ?? 'error',
      error.message ?? `Request failed with ${res.status}`,
      payload.fields as Record<string, string[]> | undefined,
    );
  }
  return payload as T;
}

/**
 * Two-step upload: ask the API for a presigned POST, then send the bytes
 * straight to S3. The file never passes through Lambda, so there is no payload
 * ceiling and no compute billed for the transfer.
 */
export async function uploadAsset(
  file: File,
  prefix: string,
  onProgress?: (fraction: number) => void,
): Promise<{ key: string; publicUrl: string }> {
  const presigned = await request<{
    key: string;
    url: string;
    fields: Record<string, string>;
    publicUrl: string;
  }>('/assets/upload-url', {
    method: 'POST',
    body: {
      fileName: file.name,
      contentType: file.type,
      contentLength: file.size,
      prefix,
    },
  });

  const form = new FormData();
  for (const [name, value] of Object.entries(presigned.fields)) form.append(name, value);
  form.append('file', file);

  await new Promise<void>((resolve, reject) => {
    // XHR rather than fetch: it is still the only way to observe upload
    // progress, which matters for the large hero images editors deal with.
    const xhr = new XMLHttpRequest();
    xhr.open('POST', presigned.url);
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total);
    });
    xhr.addEventListener('load', () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status}). ${xhr.responseText.slice(0, 200)}`)),
    );
    xhr.addEventListener('error', () => reject(new Error('Upload failed — check your connection')));
    xhr.send(form);
  });

  return { key: presigned.key, publicUrl: presigned.publicUrl };
}
